import React, { useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { Button, Badge, Spinner } from "react-bootstrap";
import { Edit2, Trash2, Settings } from "react-feather";

import { useConfirmation } from "../../contexts/confirmationContext";
import DataTable from "../../components/table";
import apiService from "../../services/apiService";

const ManageAddress = ({
  addresses,
  setAddresses,
  totalRecords,
  page,
  handlePageChange,
  handleSearchTerm,
  showNotification,
  isLoading,
  isAdminView,
  userRole,
}) => {
  const { showConfirmation } = useConfirmation();
  const [loadingState, setLoadingState] = useState({ action: "", id: "" }); // Single loading state

  // Handle delete address
  const handleDeleteAddress = async (id) => {
    showConfirmation({
      title: "Delete Address",
      description: `Are you sure you want to delete this address? This action cannot be undone and the address will be permanently removed from the user's profile.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "primary",
      onConfirm: async () => {
        setLoadingState({ action: "delete", id: id });
        try {
          await apiService.delete(`/addresses/${id}`);
          if (isAdminView) handlePageChange(1, page.PageSize);
          else {
            setAddresses((prev) => prev.filter((addr) => addr.id !== id));
          }
          showNotification(
            "success",
            "The address has been successfully removed from the user's profile."
          );
        } catch (error) {
          showNotification("danger", error || "Failed to delete address.");
        } finally {
          setLoadingState(null);
        }
      },
    });
  };

  // Handle set default address
  const setDefaultAddress = async (addressId, refUserId, type) => {
    showConfirmation({
      title: "Change Default Address",
      description: `Are you sure you want to set this address as the default? This will replace the current default address.`,
      confirmText: "Set as Default",
      cancelText: "Cancel",
      variant: "primary",
      onConfirm: async () => {
        setLoadingState({ action: "settings", id: addressId });
        try {
          await apiService.put(`/addresses/${addressId}/default`, {
            type: type,
            isDefault: true,
            userId: refUserId,
          });
          setAddresses((prev) =>
            prev.map((addr) => ({
              ...addr,
              isDefault: addr.id === addressId ? true : false,
            }))
          );
          showNotification(
            "success",
            "The selected address has been successfully set as the default."
          );
        } catch (error) {
          showNotification("danger", error || "Failed to set default address.");
        } finally {
          setLoadingState(null);
        }
      },
    });
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: (info) =>
        `${info.row.original.firstName} ${info.row.original.lastName}`,
    },
    {
      accessorKey: "address",
      header: "Address",
      cell: (info) => (
        <Fragment>
          {info.row.original.address1}, {info.row.original.address2},<br />
          {info.row.original.city}, {info.row.original.state},{" "}
          {info.row.original.country} - {info.row.original.zipCode}
        </Fragment>
      ),
    },
    { accessorKey: "phone", header: "Phone" },
    {
      accessorKey: "type",
      header: "Type",
      cell: (info) => (
        <Badge
          bg={info.getValue() === "home" ? "primary" : "secondary"}
          className="text-uppercase"
        >
          {info.getValue()}
        </Badge>
      ),
    },
    {
      accessorKey: "isDefault",
      header: "Default",
      cell: (info) => (
        <Badge bg={info.getValue() ? "primary" : "secondary"}>
          {info.getValue() ? "YES" : "NO"}
        </Badge>
      ),
    },
  ];

  if ((isAdminView && userRole === "super_admin") || !isAdminView) {
    columns.push({
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <div>
          <Link
            title="Edit Address"
            to={`/address/edit/${info.row.original.userId}/${info.row.original.id}`}
            className="btn btn-primary btn-sm me-1 mb-1"
          >
            <Edit2 size={16} />
          </Link>
          <Button
            variant={info.row.original.isDefault ? "success" : "secondary"}
            size="sm"
            title="Set Default Address"
            onClick={() =>
              setDefaultAddress(
                info.row.original.id,
                info.row.original.userId,
                info.row.original.type
              )
            }
            disabled={
              info.row.original.isDefault ||
              (loadingState?.action === "settings" &&
                loadingState.id === info.row.original.id)
            }
            className="me-1 mb-1"
          >
            {loadingState?.action === "settings" &&
            loadingState.id === info.row.original.id ? (
              <Spinner animation="border" role="status" size="sm">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              <Settings size={16} />
            )}
          </Button>
          <Button
            variant="danger"
            size="sm"
            title="Delete"
            onClick={() => handleDeleteAddress(info.row.original.id)}
            className="me-1 mb-1"
            disabled={
              loadingState?.action === "delete" &&
              loadingState.id === info.row.original.id
            }
          >
            {loadingState?.action === "delete" &&
            loadingState.id === info.row.original.id ? (
              <Spinner animation="border" role="status" size="sm">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              <Trash2 size={16} />
            )}
          </Button>
        </div>
      ),
    });
  }

  return (
    <Fragment>
      <DataTable
        columns={columns}
        data={addresses}
        totalRecords={totalRecords}
        page={page}
        onPageChange={handlePageChange}
        searchRecords={handleSearchTerm}
        loadingState={isLoading}
        searchPlaceholder="Search addresses..."
      />
    </Fragment>
  );
};

export default ManageAddress;
